/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import 'rc-dropdown/assets/index.css';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { PACE_MENU_ITEMS, formatPaceLabel } from '../../utils/pacingPercentages';
import { claimArrowKeys } from '../../utils/arrowKeyOwnership';

const DropdownIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const DropdownContainerPrimary = styled('div')`
  display: inline-flex;
  align-items: center;
  ${(props) => props.visible && `
    cursor: pointer;
    /* Even padding around the label for the focus bubble (matches lifts);
       negative margin keeps layout unchanged. The table's scroll
       padding-left gives the ring room so it isn't clipped. */
    padding: 2px 4px;
    margin: -2px -4px;
    border-radius: 4px;
    &:focus { outline: none; }
    &:focus-visible {
      outline: none;
      background: #F0F4FA;
      box-shadow: inset 0 0 0 2px #BFDBFE;
    }
  `}
`;

const MenuWrapper = styled('div')`
  display: flex;
  flex-direction: column;
`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 8px 4px;
  background: #EEEEEE;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  background: ${(props) => props.backgroundColor && props.backgroundColor};
  border-radius: 4px;
  padding: 5px;
  :hover {
    background: ${(props) => props.isSelectable && '#FCFCFC'}
  }
`;

const DeleteIconContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 4px;
`;

const ArrowDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12pt;
  color: ${(props) => (props.color)};
  margin-bottom: 0px;
  font-size: 16px;
`;

const SwapPropDropdownSAQC = ({
  size,
  primaryRowSwitch,
  setPrimaryRowSwitch,
  rowName,
  updateColumnName,
  setIntensityUnit,
  setRestPerRepUnit,
  setPaceUnit,
  paceUnit,
  name,
  unit,
  visible,
}) => {
  const menuConfig = [
    {
      condition: rowName === 'pace',
      items: PACE_MENU_ITEMS.map((paceName) => ({
        label: formatPaceLabel(paceName),
        value: paceName,
        isSelectable: paceName !== 'Running' && paceName !== 'Rowing',
        selected: paceUnit === paceName,
        action: () => setPaceUnit(paceName),
      })),
    },
    {
      condition: ['intensity (bpm)', 'intensity (RPE)', 'intensity (%)', 'intensity (W)'].includes(rowName),
      items: [
        {
          label: 'Heart Rate (bpm)',
          intensityType: 'bpm',
          selected: primaryRowSwitch['intensity (bpm)'],
          isSelectable: true,
        },
        {
          label: 'RPE',
          intensityType: 'RPE',
          selected: primaryRowSwitch['intensity (RPE)'],
          isSelectable: true,
        },
        {
          label: 'Percentage (%)',
          intensityType: '%',
          selected: primaryRowSwitch['intensity (%)'],
          isSelectable: true,
        },
        {
          label: 'Watts (W)',
          intensityType: 'W',
          selected: primaryRowSwitch['intensity (W)'],
          isSelectable: true,
        },
      ].map(({
        label, intensityType, selected, isSelectable,
      }) => ({
        label,
        selected,
        isSelectable,
        action: () => {
          setIntensityUnit(intensityType);
          updateColumnName(rowName, `intensity (${intensityType})`);
        },
      })),
    },
    {
      condition: ['rest per rep (secs)', 'rest per rep (bpm)'].includes(rowName),
      items: [
        {
          label: 'Seconds (s)',
          restUnitType: 'secs',
          selected: primaryRowSwitch['rest per rep (secs)'],
          isSelectable: true,
        },
        {
          label: 'Heart Rate (bpm)',
          restUnitType: 'bpm',
          selected: primaryRowSwitch['rest per rep (bpm)'],
          isSelectable: true,
        },
      ].map(({
        label, restUnitType, selected, isSelectable,
      }) => ({
        label,
        selected,
        isSelectable,
        action: () => {
          setRestPerRepUnit(restUnitType);
          updateColumnName(rowName, `rest per rep (${restUnitType})`);
        },
      })),
    },
  ];

  // Get the format items based on the condition
  const formatItems = menuConfig
    .filter((config) => config.condition)
    .flatMap((config) => config.items);

  const isDeleteOnly = rowName === 'prescribed value' || rowName === 'rest' || rowName === 'reps';

  const deleteAction = () => {
    const flippedStatus = !primaryRowSwitch[rowName];
    setPrimaryRowSwitch({ ...primaryRowSwitch, [rowName]: flippedStatus });
  };

  /** Controlled open + keyboard navigation (arrow/Enter/Escape), since
   * rc-menu doesn't take focus on open. Navigable items are the
   * selectable format options plus the Delete row. */
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const navItems = isDeleteOnly
    ? [{ action: deleteAction }]
    : [...formatItems.filter((i) => i.isSelectable), { action: deleteAction }];

  useEffect(() => {
    if (open) setFocusedIndex(0);
  }, [open]);

  /** Own the arrow keys while open, so useCalendarKeyboardScroll leaves the
   * calendar where it is instead of sliding a row out from under the menu. */
  useEffect(() => {
    if (!open) return undefined;
    return claimArrowKeys();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((p) => Math.min(p + 1, navItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((p) => Math.max(p - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = navItems[focusedIndex];
        if (item) { item.action(); setOpen(false); }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, focusedIndex, navItems]);

  const menu = (
    <Menu
      style={{
        padding: 8,
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: '1px solid #eeeeee',
        gap: 8,
        background: 'white',
        maxHeight: formatItems.length > 10 ? 300 : 'auto',
        scrollbarWidth: 'thin',
      }}
    >
      <MenuWrapper>
        {(() => {
          let navIndex = -1;
          return formatItems.map(({
            label, value, action, selected, isSelectable,
          }, index) => {
            if (isSelectable) navIndex += 1;
            const isFocused = isSelectable && navIndex === focusedIndex;
            return (
              <MenuItem
                key={value || label || index}
                style={{
                  fontWeight: (value === 'Running' || value === 'Rowing') && 700,
                  paddingLeft: (rowName === 'pace' && value !== 'Running' && value !== 'Rowing') && 12,
                  paddingRight: (rowName === 'pace' && value !== 'Running' && value !== 'Rowing') && 12,
                  cursor: (isSelectable && value !== 'Running' && value !== 'Rowing') ? 'pointer' : 'default',
                  color: (isSelectable && value !== 'Running' && value !== 'Rowing') ? '#444444' : '#9E9E9E',
                  background: isFocused ? '#F5F5F5' : 'white',
                }}
            /** Close on selection like the keyboard Enter path — with a
             * controlled `visible` rc-dropdown won't close itself. */
                onClick={isSelectable ? () => { action(); setOpen(false); } : undefined}
              >
                <IconContainer isSelectable={isSelectable && value !== 'Running' && value !== 'Rowing'}>
                  <ArrowDiv>
                    <MenuText color={(isSelectable && value !== 'Running' && value !== 'Rowing' && !selected) ? '#444444' : '#9E9E9E'}>{label}</MenuText>
                    {selected && isSelectable && (
                    <IcomoonReact iconSet={iconSet} size={12} icon='checkmark' color='#9E9E9E' />
                    )}
                  </ArrowDiv>
                </IconContainer>
              </MenuItem>
            );
          });
        })()}
        {formatItems.length > 0 && <CustomDivider />}
        <MenuItem
          style={{
            cursor: 'pointer', display: 'flex', width: '100%', background: focusedIndex === navItems.length - 1 ? '#F5F5F5' : 'white',
          }}
          onClick={() => { deleteAction(); setOpen(false); }}
        >
          <IconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='trashcan'
              color='#FF0505'
              style={{ marginRight: 5 }}
            />
            <ArrowDiv>
              <MenuText>Delete</MenuText>
            </ArrowDiv>
          </IconContainer>
        </MenuItem>
      </MenuWrapper>
    </Menu>
  );

  const deleteOnlyMenu = (
    <Menu
      style={{
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: '1px solid #eeeeee',
        background: 'white',
      }}
    >
      <MenuWrapper>
        <MenuItem
          style={{ padding: 8, cursor: 'pointer', background: focusedIndex === 0 ? '#F5F5F5' : 'white' }}
          onClick={() => { deleteAction(); setOpen(false); }}
        >
          <DeleteIconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='trashcan'
              color='#FF0505'
              style={{ marginRight: 5 }}
            />
            <ArrowDiv>
              <MenuText>Delete</MenuText>
            </ArrowDiv>
          </DeleteIconContainer>
        </MenuItem>
      </MenuWrapper>
    </Menu>
  );

  return (
    <Dropdown
      trigger={visible ? ['click'] : []} /** Disable trigger if not visible */
      visible={open}
      onVisibleChange={setOpen}
      overlay={isDeleteOnly ? deleteOnlyMenu : menu}
      /** rc-dropdown's stylesheet defaults the overlay to z-index 1070, which
       * sits *below* BaseModal's 1100 — the menu would render behind the modal
       * and read as an unclickable caret. Matches SwapPropDropdown. */
      overlayStyle={{ zIndex: 1101 }}
      aria-haspopup='true'
    >
      <DropdownContainerPrimary
        onClick={(e) => e.stopPropagation()}
        visible={visible}
        {...(visible ? {
          role: 'button',
          tabIndex: 0,
          onKeyDown: (e) => {
            if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
              e.preventDefault();
              e.stopPropagation();
              setOpen(true);
            }
          },
        } : {})}
      >
        <DropdownIcon padding={size}>
          {name}
          {' '}
          {unit || null}
          {!visible ? null : (
            <IcomoonReact
              iconSet={iconSet}
              size={7}
              style={{ minWidth: '7px' }}
              icon='down-arrow'
              color='black'
            />
          )}
        </DropdownIcon>
      </DropdownContainerPrimary>
    </Dropdown>
  );
};

SwapPropDropdownSAQC.propTypes = {
  size: PropTypes.number.isRequired,
  primaryRowSwitch: PropTypes.instanceOf(Object).isRequired,
  setPrimaryRowSwitch: PropTypes.func.isRequired,
  rowName: PropTypes.string.isRequired,
  updateColumnName: PropTypes.func.isRequired,
  setIntensityUnit: PropTypes.func.isRequired,
  setRestPerRepUnit: PropTypes.func.isRequired,
  setPaceUnit: PropTypes.func.isRequired,
  paceUnit: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
  visible: PropTypes.bool,
};

SwapPropDropdownSAQC.defaultProps = {
  visible: true,
};

export default SwapPropDropdownSAQC;
